import type { Spec as VegaSpec } from 'vega';
import type { TopLevelSpec } from 'vega-lite';

const NESTED_SPEC_KEYS = ['layer', 'concat', 'hconcat', 'vconcat', 'spec'];
const VEGA_LITE_SPEC_KEYS = [
  'mark',
  'encoding',
  'layer',
  'hconcat',
  'vconcat',
  'facet',
  'repeat',
];

export const isVegaLiteSpec = (spec: Record<string, unknown>): boolean => {
  if (spec.$schema) {
    return (
      typeof spec.$schema === 'string' && spec.$schema.includes('vega-lite')
    );
  }

  return VEGA_LITE_SPEC_KEYS.some(key => key in spec);
};

const getMarkType = (mark: unknown): string | undefined => {
  if (typeof mark === 'string') return mark;
  if (typeof mark !== 'object' || mark === null) return undefined;

  const markType = (mark as Record<string, unknown>).type;
  return typeof markType === 'string' ? markType : undefined;
};

// Recursively inject compact number formatting ("~s": SI prefix, e.g. 14M, 50K)
// on any quantitative axis encoding that does not already have an explicit format.
// Only touches `type: "quantitative"` channels so temporal/ordinal axes are unaffected.
export function injectCompactAxisFormat(spec: Record<string, unknown>): void {
  if (spec.encoding && typeof spec.encoding === 'object') {
    const encoding = spec.encoding as Record<string, unknown>;
    for (const channel of ['x', 'y', 'x2', 'y2']) {
      const enc = encoding[channel] as Record<string, unknown> | undefined;
      if (enc && enc.type === 'quantitative') {
        const axis = (enc.axis as Record<string, unknown> | null) ?? {};
        if (!axis.format) {
          enc.axis = { ...axis, format: '~s' };
        }
      }
    }
  }

  for (const key of NESTED_SPEC_KEYS) {
    const child = spec[key];
    if (Array.isArray(child)) {
      for (const sub of child) {
        if (typeof sub === 'object' && sub !== null) {
          injectCompactAxisFormat(sub as Record<string, unknown>);
        }
      }
    } else if (typeof child === 'object' && child !== null) {
      injectCompactAxisFormat(child as Record<string, unknown>);
    }
  }
}

const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;
const DATE_FORMAT_DIRECTIVE = /%(?:a|A|b|B|d|e|g|G|j|m|q|u|U|V|w|W|x|y|Y)/;
const TIME_FORMAT_DIRECTIVE = /%(?:c|f|H|I|L|M|p|Q|s|S|X)/;
const DATE_ONLY_VALUE = /^\d{4}-\d{2}-\d{2}$/;
const TEMPORAL_TICK_PROPERTIES = ['tickCount', 'tickMinStep', 'values'];

const hasDateOnlyFormat = (format: unknown): format is string =>
  typeof format === 'string' &&
  DATE_FORMAT_DIRECTIVE.test(format) &&
  !TIME_FORMAT_DIRECTIVE.test(format);

const getInlineDataRows = (
  spec: Record<string, unknown>,
  inheritedRows: Record<string, unknown>[] = [],
  datasets: Record<string, unknown> | undefined = undefined,
): Record<string, unknown>[] => {
  const data = spec.data;
  if (data === undefined) return inheritedRows;

  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    return [];
  }

  const dataDefinition = data as Record<string, unknown>;
  const values =
    typeof dataDefinition.name === 'string' && datasets !== undefined
      ? datasets[dataDefinition.name]
      : dataDefinition.values;
  if (!Array.isArray(values)) return [];

  if (
    !values.every(
      (value): value is Record<string, unknown> =>
        typeof value === 'object' && value !== null && !Array.isArray(value),
    )
  ) {
    return [];
  }

  return values;
};

const CONFIDENCE_PRESENTATION_CHANNELS = ['color', 'fill', 'stroke'];
const POSITION_CHANNELS = ['x', 'y'];
const CONFIDENCE_OVERFLOW_CATEGORICAL_SCHEME = 'tableau20';
const CONFIDENCE_LINE_DASH_PATTERNS = [
  [],
  [8, 4],
  [2, 3],
  [10, 3, 2, 3],
  [1, 4],
  [12, 3, 3, 3],
  [6, 2, 1, 2],
  [5, 5],
];

const hasExplicitStrokeDash = (mark: unknown): boolean =>
  typeof mark === 'object' &&
  mark !== null &&
  !Array.isArray(mark) &&
  'strokeDash' in mark;

const getStrokeLegend = (legend: unknown): Record<string, unknown> | null =>
  legend === null
    ? null
    : {
        symbolType: 'stroke',
        ...(typeof legend === 'object' && !Array.isArray(legend) ? legend : {}),
      };

type CategoricalEncodingType = 'nominal' | 'ordinal';
const CATEGORICAL_ENCODING_TYPES: readonly string[] = ['nominal', 'ordinal'];

const isCategoricalEncoding = (
  encodedField: Record<string, unknown>,
): encodedField is Record<string, unknown> & {
  type: CategoricalEncodingType;
} =>
  typeof encodedField.type === 'string' &&
  CATEGORICAL_ENCODING_TYPES.includes(encodedField.type);

type CategoricalPosition = {
  field: string;
  type: CategoricalEncodingType;
};

const getCategoricalPosition = (
  encoding: Record<string, unknown>,
): CategoricalPosition | undefined => {
  for (const channel of POSITION_CHANNELS) {
    const encodedField = encoding[channel];
    if (typeof encodedField !== 'object' || encodedField === null) continue;

    const fieldDefinition = encodedField as Record<string, unknown>;
    if (
      typeof fieldDefinition.field === 'string' &&
      isCategoricalEncoding(fieldDefinition)
    ) {
      return {
        field: fieldDefinition.field,
        type: fieldDefinition.type,
      };
    }
  }

  return undefined;
};

const getDistinctValueKey = (value: unknown): unknown => {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value);
  }
  return value;
};

const getDistinctValueCount = (
  rows: Record<string, unknown>[],
  field: string,
): number =>
  new Set(
    rows
      .map(row => row[field])
      .map(getDistinctValueKey)
      .filter(value => value !== null && value !== undefined),
  ).size;

type ConfidenceTransformView = {
  initialize: () => ConfidenceTransformView;
  run: () => ConfidenceTransformView;
  data: (name: string) => Record<string, unknown>[];
  finalize: () => void;
};

export type ConfidenceSeriesTransformRuntime = {
  compile: (spec: TopLevelSpec) => VegaSpec;
  createView: (spec: VegaSpec) => ConfidenceTransformView;
};

type ConfidenceSeriesPalettes = {
  categorical: readonly string[];
  sequential: readonly string[];
};

type RepeatValues = Record<string, readonly unknown[]>;

const applyConfidenceSeriesTransforms = (
  rows: Record<string, unknown>[],
  transform: unknown,
  runtime: ConfidenceSeriesTransformRuntime | undefined,
): Record<string, unknown>[] => {
  if (!Array.isArray(transform) || runtime === undefined) return rows;

  let view: ConfidenceTransformView | undefined;
  try {
    const transformedSpec = {
      data: { values: rows.map(row => ({ ...row })) },
      transform,
      mark: 'point',
    } as TopLevelSpec;
    const compiledSpec = runtime.compile(transformedSpec);
    const firstMark = compiledSpec.marks?.[0];
    const dataName = firstMark?.from?.data;
    if (typeof dataName !== 'string') return rows;

    view = runtime.createView(compiledSpec);
    view.initialize().run();
    return view.data(dataName).map(row => ({ ...row }));
  } catch {
    return rows;
  } finally {
    view?.finalize();
  }
};

const getRepeatValues = (
  repeat: unknown,
  inheritedRepeatValues: RepeatValues,
): RepeatValues => {
  if (Array.isArray(repeat)) {
    return { ...inheritedRepeatValues, repeat };
  }
  if (typeof repeat !== 'object' || repeat === null) {
    return inheritedRepeatValues;
  }

  return Object.entries(repeat).reduce<RepeatValues>(
    (repeatValues, [key, values]) =>
      Array.isArray(values) ? { ...repeatValues, [key]: values } : repeatValues,
    inheritedRepeatValues,
  );
};

const getCategoricalEncodingValues = (
  encoding: Record<string, unknown>,
  rows: Record<string, unknown>[],
  repeatValues: RepeatValues,
): readonly unknown[] | undefined => {
  if (!isCategoricalEncoding(encoding)) return undefined;
  if ('datum' in encoding) return [encoding.datum];

  const field = encoding.field;
  if (typeof field === 'string') {
    return rows.length > 0 ? rows.map(row => row[field]) : undefined;
  }
  if (typeof field !== 'object' || field === null || Array.isArray(field)) {
    return undefined;
  }

  const repeat = (field as Record<string, unknown>).repeat;
  return typeof repeat === 'string' ? repeatValues[repeat] : undefined;
};

const hasAuthoredScale = (encoding: Record<string, unknown>): boolean =>
  'scale' in encoding;

const hasExplicitMarkPresentation = (mark: unknown): boolean =>
  typeof mark === 'object' &&
  mark !== null &&
  !Array.isArray(mark) &&
  CONFIDENCE_PRESENTATION_CHANNELS.some(
    channel => (mark as Record<string, unknown>)[channel] !== undefined,
  );

const removeRedundantBarValueColor = (
  localEncoding: Record<string, unknown>,
  encoding: Record<string, unknown>,
): void => {
  const quantitativePositionFields = new Set(
    POSITION_CHANNELS.flatMap(channel => {
      const encodedField = encoding[channel];
      if (typeof encodedField !== 'object' || encodedField === null) return [];

      const fieldDefinition = encodedField as Record<string, unknown>;
      return fieldDefinition.type === 'quantitative' &&
        typeof fieldDefinition.field === 'string'
        ? [fieldDefinition.field]
        : [];
    }),
  );

  for (const channel of CONFIDENCE_PRESENTATION_CHANNELS) {
    const encodedField = encoding[channel];
    if (typeof encodedField !== 'object' || encodedField === null) continue;

    const fieldDefinition = encodedField as Record<string, unknown>;
    if (
      fieldDefinition.type === 'quantitative' &&
      typeof fieldDefinition.field === 'string' &&
      quantitativePositionFields.has(fieldDefinition.field)
    ) {
      Reflect.deleteProperty(localEncoding, channel);
      Reflect.deleteProperty(encoding, channel);
    }
  }
};

const normalizeCategoricalBarValueScale = (
  encoding: Record<string, unknown>,
): void => {
  if (encoding.x2 !== undefined || encoding.y2 !== undefined) return;

  for (const [categoryChannel, valueChannel] of [
    ['x', 'y'],
    ['y', 'x'],
  ]) {
    const categoryEncoding = encoding[categoryChannel];
    const valueEncoding = encoding[valueChannel];
    if (
      typeof categoryEncoding !== 'object' ||
      categoryEncoding === null ||
      typeof valueEncoding !== 'object' ||
      valueEncoding === null
    ) {
      continue;
    }

    const categoryFieldDefinition = categoryEncoding as Record<string, unknown>;
    const valueFieldDefinition = valueEncoding as Record<string, unknown>;
    if (
      !isCategoricalEncoding(categoryFieldDefinition) ||
      typeof categoryFieldDefinition.field !== 'string' ||
      valueFieldDefinition.type !== 'quantitative' ||
      valueFieldDefinition.scale === null
    ) {
      continue;
    }

    const scale = valueFieldDefinition.scale;
    valueFieldDefinition.scale = {
      ...(typeof scale === 'object' && !Array.isArray(scale) ? scale : {}),
      zero: true,
    };
    return;
  }
};

const getConfidenceSeriesScale = (
  distinctValueCount: number,
  encodingType: CategoricalEncodingType,
  palettes: ConfidenceSeriesPalettes,
): Record<string, unknown> | undefined => {
  if (distinctValueCount < 1) return undefined;
  if (
    encodingType === 'ordinal' &&
    distinctValueCount <= palettes.sequential.length
  ) {
    return { range: [...palettes.sequential] };
  }

  return distinctValueCount <= palettes.categorical.length
    ? { range: [...palettes.categorical] }
    : { scheme: CONFIDENCE_OVERFLOW_CATEGORICAL_SCHEME };
};

const getDatasets = (
  spec: Record<string, unknown>,
  inheritedDatasets: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined =>
  typeof spec.datasets === 'object' &&
  spec.datasets !== null &&
  !Array.isArray(spec.datasets)
    ? (spec.datasets as Record<string, unknown>)
    : inheritedDatasets;

const getTransformedRows = (
  spec: Record<string, unknown>,
  inheritedRows: Record<string, unknown>[],
  datasets: Record<string, unknown> | undefined,
  transformRuntime: ConfidenceSeriesTransformRuntime | undefined,
): Record<string, unknown>[] =>
  applyConfidenceSeriesTransforms(
    getInlineDataRows(spec, inheritedRows, datasets),
    spec.transform,
    transformRuntime,
  );

const injectLineDashStyle = (
  spec: Record<string, unknown>,
  localEncoding: Record<string, unknown>,
  encoding: Record<string, unknown>,
  categoricalEncoding: Record<string, unknown>,
  distinctValueCount: number,
  palettes: ConfidenceSeriesPalettes,
): void => {
  const seriesField = categoricalEncoding.field;
  if (
    getMarkType(spec.mark) !== 'line' ||
    seriesField === undefined ||
    distinctValueCount <= palettes.sequential.length ||
    distinctValueCount > CONFIDENCE_LINE_DASH_PATTERNS.length
  ) {
    return;
  }

  if (encoding.strokeDash === undefined && !hasExplicitStrokeDash(spec.mark)) {
    localEncoding.strokeDash = {
      field: seriesField,
      type: categoricalEncoding.type,
      scale: {
        range: CONFIDENCE_LINE_DASH_PATTERNS.map(pattern => [...pattern]),
      },
    };
    spec.encoding = localEncoding;
  }

  const strokeDashEncoding = localEncoding.strokeDash ?? encoding.strokeDash;
  if (
    typeof strokeDashEncoding === 'object' &&
    strokeDashEncoding !== null &&
    !Array.isArray(strokeDashEncoding) &&
    (strokeDashEncoding as Record<string, unknown>).field === seriesField
  ) {
    const lineLegend = getStrokeLegend(categoricalEncoding.legend);
    categoricalEncoding.legend = lineLegend;
    (strokeDashEncoding as Record<string, unknown>).legend = lineLegend;
  }
};

const injectSharedLayerScales = (
  spec: Record<string, unknown>,
  palettes: ConfidenceSeriesPalettes,
  inheritedEncoding: Record<string, unknown>,
  rows: Record<string, unknown>[],
  repeatValues: RepeatValues,
  layerRowsBySpec: ReadonlyMap<
    Record<string, unknown>,
    Record<string, unknown>[]
  >,
): Map<string, number | undefined> => {
  if (!Array.isArray(spec.layer)) return new Map();

  const resolveScale =
    typeof spec.resolve === 'object' &&
    spec.resolve !== null &&
    !Array.isArray(spec.resolve) &&
    typeof (spec.resolve as Record<string, unknown>).scale === 'object' &&
    (spec.resolve as Record<string, unknown>).scale !== null
      ? ((spec.resolve as Record<string, unknown>).scale as Record<
          string,
          unknown
        >)
      : {};
  const sharedChannels = new Map<string, number | undefined>();

  for (const channel of CONFIDENCE_PRESENTATION_CHANNELS) {
    if (resolveScale[channel] === 'independent') continue;

    const entries = spec.layer.flatMap(layer => {
      if (typeof layer !== 'object' || layer === null || Array.isArray(layer)) {
        return [];
      }
      const layerSpec = layer as Record<string, unknown>;
      const localEncoding =
        typeof layerSpec.encoding === 'object' &&
        layerSpec.encoding !== null &&
        !Array.isArray(layerSpec.encoding)
          ? (layerSpec.encoding as Record<string, unknown>)
          : {};
      const encoding = { ...inheritedEncoding, ...localEncoding }[channel];
      if (typeof encoding !== 'object' || encoding === null) return [];

      const categoricalEncoding = encoding as Record<string, unknown>;
      if (!isCategoricalEncoding(categoricalEncoding)) return [];
      const layerRows = layerRowsBySpec.get(layerSpec) ?? rows;
      return [
        {
          encoding: categoricalEncoding,
          values: getCategoricalEncodingValues(
            categoricalEncoding,
            layerRows,
            repeatValues,
          ),
        },
      ];
    });

    if (entries.length < 2) continue;
    sharedChannels.set(channel, undefined);
    if (
      entries.some(entry => hasAuthoredScale(entry.encoding)) ||
      entries.some(entry => entry.values === undefined)
    ) {
      continue;
    }

    const distinctValues = new Set(
      entries
        .flatMap(entry => entry.values ?? [])
        .map(getDistinctValueKey)
        .filter(value => value !== null && value !== undefined),
    );
    const encodingType = entries.every(
      entry => entry.encoding.type === 'ordinal',
    )
      ? 'ordinal'
      : 'nominal';
    const scale = getConfidenceSeriesScale(
      distinctValues.size,
      encodingType,
      palettes,
    );
    if (scale === undefined) continue;
    sharedChannels.set(channel, distinctValues.size);

    for (const encoding of new Set(entries.map(entry => entry.encoding))) {
      encoding.scale = { ...scale };
    }
  }

  return sharedChannels;
};

const injectConfidenceSeriesStylesInSpec = (
  spec: Record<string, unknown>,
  palettes: ConfidenceSeriesPalettes,
  inheritedEncoding: Record<string, unknown> = {},
  inheritedRows: Record<string, unknown>[] = [],
  inheritedDatasets: Record<string, unknown> | undefined = undefined,
  transformRuntime: ConfidenceSeriesTransformRuntime | undefined = undefined,
  inheritedRepeatValues: RepeatValues = {},
  inheritedSharedChannels: ReadonlyMap<string, number | undefined> = new Map(),
  precomputedRows: Record<string, unknown>[] | undefined = undefined,
): void => {
  const datasets = getDatasets(spec, inheritedDatasets);
  const rows =
    precomputedRows ??
    getTransformedRows(spec, inheritedRows, datasets, transformRuntime);
  const localEncoding =
    typeof spec.encoding === 'object' && spec.encoding !== null
      ? (spec.encoding as Record<string, unknown>)
      : {};
  const encoding = { ...inheritedEncoding, ...localEncoding };
  const markType = getMarkType(spec.mark);
  const repeatValues = getRepeatValues(spec.repeat, inheritedRepeatValues);
  const layerRowsBySpec = new Map<
    Record<string, unknown>,
    Record<string, unknown>[]
  >();
  if (Array.isArray(spec.layer)) {
    for (const layer of spec.layer) {
      if (typeof layer !== 'object' || layer === null || Array.isArray(layer)) {
        continue;
      }
      const layerSpec = layer as Record<string, unknown>;
      layerRowsBySpec.set(
        layerSpec,
        getTransformedRows(
          layerSpec,
          rows,
          getDatasets(layerSpec, datasets),
          transformRuntime,
        ),
      );
    }
  }
  const layerSharedChannels = injectSharedLayerScales(
    spec,
    palettes,
    encoding,
    rows,
    repeatValues,
    layerRowsBySpec,
  );
  const sharedChannels = new Map([
    ...inheritedSharedChannels,
    ...layerSharedChannels,
  ]);

  if (markType === 'bar') {
    removeRedundantBarValueColor(localEncoding, encoding);
    normalizeCategoricalBarValueScale(encoding);
  }

  if (markType !== undefined) {
    for (const channel of CONFIDENCE_PRESENTATION_CHANNELS) {
      const encodedField = encoding[channel];
      if (typeof encodedField !== 'object' || encodedField === null) continue;

      const categoricalEncoding = encodedField as Record<string, unknown>;
      if (!isCategoricalEncoding(categoricalEncoding)) continue;
      if (sharedChannels.has(channel)) {
        const distinctValueCount = sharedChannels.get(channel);
        if (distinctValueCount !== undefined) {
          injectLineDashStyle(
            spec,
            localEncoding,
            encoding,
            categoricalEncoding,
            distinctValueCount,
            palettes,
          );
        }
        continue;
      }
      const values = getCategoricalEncodingValues(
        categoricalEncoding,
        rows,
        repeatValues,
      );
      if (values === undefined || hasAuthoredScale(categoricalEncoding)) {
        continue;
      }
      const distinctValueCount = new Set(
        values
          .map(getDistinctValueKey)
          .filter(value => value !== null && value !== undefined),
      ).size;
      const scale = getConfidenceSeriesScale(
        distinctValueCount,
        categoricalEncoding.type,
        palettes,
      );
      if (scale === undefined) continue;

      categoricalEncoding.scale = scale;
      injectLineDashStyle(
        spec,
        localEncoding,
        encoding,
        categoricalEncoding,
        distinctValueCount,
        palettes,
      );
      break;
    }
  }

  if (
    markType === 'bar' &&
    !hasExplicitMarkPresentation(spec.mark) &&
    !CONFIDENCE_PRESENTATION_CHANNELS.some(
      channel => encoding[channel] !== undefined,
    )
  ) {
    const categoryPosition = getCategoricalPosition(encoding);
    if (categoryPosition !== undefined) {
      const distinctValueCount = getDistinctValueCount(
        rows,
        categoryPosition.field,
      );
      if (distinctValueCount >= 2) {
        const seriesScale = getConfidenceSeriesScale(
          distinctValueCount,
          categoryPosition.type,
          palettes,
        );
        localEncoding.color = {
          field: categoryPosition.field,
          type: categoryPosition.type,
          ...(seriesScale !== undefined ? { scale: seriesScale } : {}),
          legend: null,
        };
      }
    }
  }

  for (const key of NESTED_SPEC_KEYS) {
    const child = spec[key];
    if (Array.isArray(child)) {
      for (const childSpec of child) {
        if (typeof childSpec === 'object' && childSpec !== null) {
          injectConfidenceSeriesStylesInSpec(
            childSpec as Record<string, unknown>,
            palettes,
            encoding,
            rows,
            datasets,
            transformRuntime,
            repeatValues,
            sharedChannels,
            layerRowsBySpec.get(childSpec as Record<string, unknown>),
          );
        }
      }
    } else if (typeof child === 'object' && child !== null) {
      injectConfidenceSeriesStylesInSpec(
        child as Record<string, unknown>,
        palettes,
        encoding,
        rows,
        datasets,
        transformRuntime,
        repeatValues,
        sharedChannels,
      );
    }
  }
};

export const injectConfidenceSeriesStyles = (
  spec: Record<string, unknown>,
  palettes: ConfidenceSeriesPalettes,
  transformRuntime?: ConfidenceSeriesTransformRuntime,
): void =>
  injectConfidenceSeriesStylesInSpec(
    spec,
    palettes,
    {},
    [],
    undefined,
    transformRuntime,
  );

const hasDateOnlyValues = (
  field: unknown,
  rows: Record<string, unknown>[],
): boolean =>
  typeof field === 'string' &&
  rows.length > 0 &&
  rows.every(row =>
    typeof row[field] === 'string'
      ? DATE_ONLY_VALUE.test(row[field] as string)
      : false,
  );

const injectUtcScale = (temporalEncoding: Record<string, unknown>): void => {
  const scale = temporalEncoding.scale;
  if (scale === undefined) {
    temporalEncoding.scale = { type: 'utc' };
  } else if (
    typeof scale === 'object' &&
    scale !== null &&
    !Array.isArray(scale) &&
    !('type' in scale)
  ) {
    temporalEncoding.scale = {
      ...(scale as Record<string, unknown>),
      type: 'utc',
    };
  }
};

const normalizeDateOnlyTemporalAxesInSpec = (
  spec: Record<string, unknown>,
  inheritedRows: Record<string, unknown>[] = [],
): void => {
  const rows = getInlineDataRows(spec, inheritedRows);
  if (spec.encoding && typeof spec.encoding === 'object') {
    const encoding = spec.encoding as Record<string, unknown>;
    for (const channel of ['x', 'y']) {
      const encodedField = encoding[channel];
      if (typeof encodedField !== 'object' || encodedField === null) continue;

      const temporalEncoding = encodedField as Record<string, unknown>;
      const axis = temporalEncoding.axis;
      if (
        temporalEncoding.type !== 'temporal' ||
        typeof axis !== 'object' ||
        axis === null
      ) {
        continue;
      }

      const temporalAxis = axis as Record<string, unknown>;
      if (!hasDateOnlyFormat(temporalAxis.format)) continue;

      if (hasDateOnlyValues(temporalEncoding.field, rows)) {
        injectUtcScale(temporalEncoding);
      }

      const hasExplicitTickConfiguration = TEMPORAL_TICK_PROPERTIES.some(
        property => property in temporalAxis,
      );
      if (!hasExplicitTickConfiguration) {
        temporalAxis.tickMinStep = DAY_IN_MILLISECONDS;
      }
    }
  }

  for (const key of NESTED_SPEC_KEYS) {
    const child = spec[key];
    if (Array.isArray(child)) {
      for (const subSpec of child) {
        if (typeof subSpec === 'object' && subSpec !== null) {
          normalizeDateOnlyTemporalAxesInSpec(
            subSpec as Record<string, unknown>,
            rows,
          );
        }
      }
    } else if (typeof child === 'object' && child !== null) {
      normalizeDateOnlyTemporalAxesInSpec(
        child as Record<string, unknown>,
        rows,
      );
    }
  }
};

export function normalizeDateOnlyTemporalAxes(
  spec: Record<string, unknown>,
): void {
  normalizeDateOnlyTemporalAxesInSpec(spec);
}

const ISO_DATE_OR_ZONED_TIMESTAMP =
  /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2}))?$/;

const asDefinition = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

const usesTemporalScale = (definition: Record<string, unknown>): boolean => {
  if (definition.scale === null || definition.timeUnit !== undefined)
    return false;
  const scaleType = asDefinition(definition.scale)?.type;
  return scaleType === undefined
    ? definition.type === 'temporal'
    : scaleType === 'time' || scaleType === 'utc';
};

const normalizeTemporalDatumsInSpec = (
  spec: Record<string, unknown>,
  inheritedEncoding: Record<string, unknown> = {},
): void => {
  const localEncoding = asDefinition(spec.encoding) ?? {};
  const encoding = { ...inheritedEncoding, ...localEncoding };

  if (spec.mark !== undefined) {
    for (const channel of ['x', 'y', 'x2', 'y2']) {
      const definition = asDefinition(encoding[channel]);
      const primaryDefinition = asDefinition(encoding[channel[0]]);
      if (
        !definition ||
        !primaryDefinition ||
        !usesTemporalScale(primaryDefinition) ||
        typeof definition.datum !== 'string' ||
        !ISO_DATE_OR_ZONED_TIMESTAMP.test(definition.datum)
      ) {
        continue;
      }

      const timestamp = Date.parse(definition.datum);
      const date = definition.datum.slice(0, 10);
      const calendarDate = Date.parse(date);
      if (
        !Number.isFinite(timestamp) ||
        !Number.isFinite(calendarDate) ||
        new Date(calendarDate).toISOString().slice(0, 10) !== date
      ) {
        continue;
      }
      localEncoding[channel] = { ...definition, datum: timestamp };
      spec.encoding = localEncoding;
    }
  }

  for (const key of NESTED_SPEC_KEYS) {
    const children = Array.isArray(spec[key]) ? spec[key] : [spec[key]];
    for (const child of children) {
      const childSpec = asDefinition(child);
      if (childSpec) {
        normalizeTemporalDatumsInSpec(
          childSpec,
          key === 'layer' ? encoding : {},
        );
      }
    }
  }
};

export const normalizeTemporalDatums = (
  spec: Record<string, unknown>,
): void => {
  if (isVegaLiteSpec(spec)) normalizeTemporalDatumsInSpec(spec);
};

const pathMarkTypes = ['line', 'area'];

const enableMarkTooltip = (spec: Record<string, unknown>): void => {
  const markValue = spec.mark;
  let mark: Record<string, unknown> | undefined;
  if (typeof markValue === 'string') {
    mark = { type: markValue };
  } else if (typeof markValue === 'object' && markValue !== null) {
    mark = markValue as Record<string, unknown>;
  }

  if (!mark) return;

  if (mark.tooltip === false) {
    spec.mark = mark;
    return;
  }

  const markType = String(mark.type);
  if (!pathMarkTypes.includes(markType)) {
    mark.tooltip ??= true;
    spec.mark = mark;
    return;
  }

  mark.tooltip = false;
  mark.point = { opacity: 0, size: 400, tooltip: true };
  spec.mark = mark;
};

const removeVisiblePointLayers = (spec: Record<string, unknown>): void => {
  if (!Array.isArray(spec.layer)) return;

  const hasPathLayer = spec.layer.some(
    layer =>
      typeof layer === 'object' &&
      layer !== null &&
      pathMarkTypes.includes(
        getMarkType((layer as Record<string, unknown>).mark) ?? '',
      ),
  );
  if (!hasPathLayer) return;

  spec.layer = spec.layer.filter(layer => {
    if (typeof layer !== 'object' || layer === null) return true;
    const markType = getMarkType((layer as Record<string, unknown>).mark);
    return markType !== 'point' && markType !== 'circle';
  });
};

const injectTooltipInteractivityInSpec = (
  spec: Record<string, unknown>,
): void => {
  removeVisiblePointLayers(spec);
  enableMarkTooltip(spec);

  for (const key of NESTED_SPEC_KEYS) {
    const child = spec[key];
    if (Array.isArray(child)) {
      for (const childSpec of child) {
        if (typeof childSpec === 'object' && childSpec !== null) {
          injectTooltipInteractivityInSpec(
            childSpec as Record<string, unknown>,
          );
        }
      }
    } else if (typeof child === 'object' && child !== null) {
      injectTooltipInteractivityInSpec(child as Record<string, unknown>);
    }
  }
};

export const injectTooltipInteractivity = (
  spec: Record<string, unknown>,
): void => {
  if (!isVegaLiteSpec(spec)) return;

  injectTooltipInteractivityInSpec(spec);
};
